import React, { forwardRef } from 'react'

import { LeftNav, type LeftNavProps } from '../../LeftNav'
import styles from './_left-nav-footer.module.scss'
import AccountPopover, {
  type AccountPopoverProps,
} from '../../../AccountPopover/AccountPopover'
import { useIsMobileView } from '../../../../module'
import LanguageSelector from '../LanguageSelector/LanguageSelector'

type LeftNavFooterProps = {
  /** There are 2 variations: collapsed and expanded. This prop determines when to show each. */
  expanded: boolean
  /** All the props needed for the AccountPopover */
  accountPopoverProps: LeftNavProps['accountPopoverProps']
  /** Optional footer element */
  footer?: LeftNavProps['footer']
  /** Function to close the mobile drawer. This is kept as optional because it is not needed in LeftNavDesktop. */
  closeMobileDrawer?: AccountPopoverProps['closeMobileDrawer']
  /** Function to close the Sidebar expanded state. This is kept as optional because it is not needed in LeftNavMobile. */
  closeSidebar?: (force?: boolean) => void
  /** Function to open the Sidebar expanded state */
  openSidebar?: () => void
  /** Shows whether LeftNavMobile is being rendered with this component */
  mobile?: boolean
  /** Language configuration */
  languageConfig?: LeftNavProps['languageConfig']
  /** Optional prop to hide the copyright */
  hideCopyright?: boolean
}

const LeftNavFooter = forwardRef<HTMLDivElement, LeftNavFooterProps>(
  (
    {
      expanded,
      accountPopoverProps,
      footer,
      closeMobileDrawer,
      closeSidebar,
      openSidebar,
      mobile,
      languageConfig,
      hideCopyright = false,
    }: LeftNavFooterProps,
    ref,
  ): React.JSX.Element => {
    const year = new Date().getFullYear(),
      isMobile = useIsMobileView()

    const Copyright = () => {
      return <span className={styles.copyright}>&copy; {year} Pattern</span>
    }

    return (
      <div className={styles.footerContainer} ref={ref}>
        {/* Divider needed for mobile */}
        {!isMobile ? <LeftNav.Divider /> : null}

        {/* `footer` is a custom "children" prop for app-specific experiences */}
        {footer?.({ sidebarExpanded: expanded })}

        {/* LanguageSelector for apps that opt-in to using language translations - in mobile it is placed to the right of Copyright */}
        {languageConfig && isMobile ? (
          <div
            className={
              hideCopyright
                ? styles.hideCopyright
                : styles.copyrightAndLanguageSelector
            }
          >
            {!hideCopyright ? (
              <>
                <Copyright />
                <div className={styles.divider} />
              </>
            ) : null}

            <LanguageSelector {...languageConfig} expanded={expanded} />
          </div>
        ) : // When there is no `languageConfig` or not mobile, show the default Copyright component
        !hideCopyright ? (
          <Copyright />
        ) : null}

        {/* Divider needed after required Copyright component */}
        {!hideCopyright || languageConfig ? <LeftNav.Divider /> : null}

        {/* LanguageSelector for apps that opt-in to using language translations - in desktop it is placed just above AccountPopover */}
        {languageConfig && !isMobile ? (
          <>
            <LanguageSelector {...languageConfig} expanded={expanded} />
            <LeftNav.Divider />
          </>
        ) : null}

        {/* AccountPopover component */}
        <AccountPopover
          {...accountPopoverProps}
          expanded={expanded}
          closeMobileDrawer={closeMobileDrawer}
          closeSidebar={closeSidebar}
          openSidebar={openSidebar}
          mobile={mobile}
        />
      </div>
    )
  },
)

LeftNavFooter.displayName = 'LeftNavFooter'
export default LeftNavFooter
